import React, {
	RefAttributes,
	memo,
	useCallback,
	useState,
	useEffect,
} from "react";
import { react, useRefWithSetter, theme, CheckBox } from "uikit";
import { useTranslation } from "react-i18next";

import { PaymentSystem, Language } from "../../../../../../../services";
import {
	PaymentProvider,
	PaymentSystemFrontEndType,
} from "../../../../../../../services/PaymentSystem";
import useObjectEditor from "../../../../../../../hooks/useObjectEditor";
import EditModalBase from "../../../../../../../components/EditModal";
import { StyledRow, StyledSpan } from "../../../../../../../components/common";
import translationPath from "../../../constants/translationPath";

import { Content, Root, Header } from "./components";
import InternalController from "./Controller";

const ModalBase = react.withController<Modal.PropsBase, Modal.Controller>(
	({
		value,
		onCancel,
		onSave,
		createdAt,
		headerTitle,
		controller,
		loading,
		errorTextAll,
		setErrorTextAll,
	}) => {
		const { t } = useTranslation();

		const [contentRef, setContentRef] =
			useRefWithSetter<Content.Ref | null>(null);

		const [internalValue, setInternalValue] = useState(value);

		useEffect(() => {
			setInternalValue(value);
		}, [value]);

		const valueEditor = useObjectEditor(internalValue, setInternalValue);
		const contentOnChange = valueEditor.useAssigner();

		const active = valueEditor.useGetter("active");
		const setActive = valueEditor.useSetter("active");
		const setEasyPayAction = valueEditor.useSetter("easyPayAction");
		const setOtvAction = valueEditor.useSetter("otvAction");
		const paymentProvider = valueEditor.useGetter("paymentProvider");

		const handleSubmit = useCallback(() => {
			if (!contentRef.current?.validate()) {
				return;
			}

			const provider =
				internalValue.paymentProvider === PaymentProvider.OTV
					? PaymentProvider.OTV
					: PaymentProvider.EASYPAY;

			setErrorTextAll?.("");
			onSave({
				...internalValue,
				type: PaymentSystemFrontEndType.TERMINAL_EXECUTOR,
				paymentProvider: provider,
				easyPayAction:
					provider === PaymentProvider.EASYPAY
						? internalValue.active
						: internalValue.easyPayAction,
				otvAction:
					provider === PaymentProvider.OTV
						? internalValue.active
						: internalValue.otvAction,
			} as PaymentSystem.Modified);
		}, [contentRef, internalValue, onSave, setErrorTextAll]);

		controller.setContext({ contentRef });

		return (
			<EditModalBase
				onCancel={onCancel}
				onSave={handleSubmit}
				loading={loading}
				footer={
					<StyledRow gap="16px" alignItems="center">
						<CheckBox
							value={!!active}
							onChange={(checked) => {
								const next = !!checked;
								setActive(next);
								if (paymentProvider === PaymentProvider.OTV) {
									setOtvAction(next);
								} else {
									setEasyPayAction(next);
								}
							}}
							label={
								t([
									`${translationPath}.modal.text.easyPayAction`,
									"Активна",
								]) || ""
							}
						/>
					</StyledRow>
				}
			>
				<Root sizes="auto! 1fr">
					<Header title={headerTitle} createdAt={createdAt} />
					<Content
						ref={setContentRef}
						value={internalValue}
						onChange={contentOnChange}
					/>
					<StyledRow p="20px 24px">
						<StyledSpan colors={theme.colors.error}>
							{errorTextAll}
						</StyledSpan>
					</StyledRow>
				</Root>
			</EditModalBase>
		);
	},
	InternalController,
);
const Modal = memo(ModalBase);

declare namespace Modal {
	type Ref = InternalController | null;
	type Controller = InternalController;

	interface PropsBase {
		value: Value;
		headerTitle: string;
		createdAt: string;
		lang: Language;
		loading?: boolean;
		onCancel: () => void;
		onSave: (template: PaymentSystem.Modified) => Promise<void>;
		errorTextAll: string;
		setErrorTextAll: (err: string) => void;
	}
	type Props = PropsBase & RefAttributes<Ref>;

	type Value = PaymentSystem.Model;
}

export default Modal;
